// MeetSuggestionConfirmModal — review-before-save dialog for AI-extracted
// suggestions on the transcript page.
//
// Flow:
//  1. User clicks a MeetSuggestionCard → root.suggestionConfirm = { bucket, item }
//  2. This modal opens, showing the extracted data.
//  3. User can edit the title (most common tweak) and reads description/meta.
//  4. "Create" / "Update" button calls applySuggestion with the (possibly edited)
//     item, then routes to the target feature.
//  5. "Cancel" (or backdrop/X) clears root.suggestionConfirm.
//
// stateKey: 'suggestionConfirm' — the Modal base component reads this as a
// truthy-check to show/hide, so any non-null value opens the modal.

import { SUGGESTION_BUCKETS, routeTo } from '../../functions/meet/meetUtils.js'

// Verb + label for the confirm button and modal title
const _verbLabel = (bucket, action) => {
  const label = (SUGGESTION_BUCKETS[bucket]?.label || bucket)
    .toLowerCase()
    .replace(/s$/, '') // "tickets" → "ticket", "notes" → "note"
  const verb = action === 'update' ? 'Update' : 'Create'
  return { verb, label }
}

// One-line metadata strip shown under the body preview
const _metaLine = (bucket, item) => {
  if (!item) return ''
  if (bucket === 'calendar_events') {
    const parts = []
    if (item.proposed_time) parts.push(`⏰ ${item.proposed_time}`)
    if (Array.isArray(item.attendees) && item.attendees.length) parts.push(item.attendees.join(', '))
    return parts.join(' · ')
  }
  if (bucket === 'tickets') return item.assignee_email ? `→ ${item.assignee_email}` : ''
  if (bucket === 'notes') return (Array.isArray(item.tags) && item.tags.length) ? item.tags.map(t => `#${t}`).join(' ') : ''
  if (bucket === 'knowledge_base') return item.category || ''
  if (bucket === 'chat_messages') return item.to_email ? `To: ${item.to_email}` : ''
  return ''
}

// The "body" field to display as a read-only preview
const _bodyText = (item) =>
  item?.description || item?.content || item?.summary || item?.body || ''

export const MeetSuggestionConfirmModal = {
  extends: 'Modal',
  stateKey: 'suggestionConfirm',
  // extraClose clears the editable-title slot when the modal is dismissed
  // via the backdrop or X button (as opposed to our own buttons that clear it).
  extraClose: { suggestionConfirmTitle: null },

  Window: {
    width: 'I',
    maxWidth: '90vw',
    gap: 'A',
    ModalHeader: {
      Hgroup: {
        H: {
          text: (el, s) => {
            const c = s.root?.suggestionConfirm
            if (!c) return 'Review suggestion'
            const { verb, label } = _verbLabel(c.bucket, c.item?.action)
            return `${verb} ${label}`
          }
        },
        P: {
          text: (el, s) => {
            const c = s.root?.suggestionConfirm
            if (!c) return ''
            const { verb, label } = _verbLabel(c.bucket, c.item?.action)
            return `${verb} a new ${label} from this transcript suggestion.`
          }
        }
      }
    },

    // "Will update existing" badge for update-action items
    ActionBadge: {
      hide: (el, s) => s.root?.suggestionConfirm?.item?.action !== 'update',
      padding: 'V Z',
      round: 'Y',
      background: 'cyan',
      color: 'black',
      fontSize: 'Y1',
      fontWeight: '700',
      letterSpacing: '.05em',
      textTransform: 'uppercase',
      text: 'Will update existing'
    },

    // Editable title (or message body for chat suggestions)
    TitleField: {
      flow: 'y',
      gap: 'V',

      Label: {
        tag: 'span',
        fontSize: 'Y2',
        color: 'caption',
        text: (el, s) => s.root?.suggestionConfirm?.bucket === 'chat_messages' ? 'MESSAGE' : 'TITLE'
      },
      Input: {
        tag: 'input',
        padding: 'Z A',
        round: 'Z',
        borderWidth: '1px',
        borderColor: 'line',
        borderStyle: 'solid',
        background: 'document',
        color: 'title',
        fontSize: 'Z2',
        value: (el, s) => s.root?.suggestionConfirmTitle || '',
        onInput: (e, el, s) => s.root?.update({ suggestionConfirmTitle: e.target.value })
      }
    },

    // Read-only description / body preview
    BodyPreview: {
      hide: (el, s) => !_bodyText(s.root?.suggestionConfirm?.item),
      flow: 'y',
      gap: 'V',

      Label: { tag: 'span', fontSize: 'Y2', color: 'caption', text: 'DETAILS' },
      Text: {
        tag: 'p',
        margin: '0',
        padding: 'Z A',
        round: 'Z',
        background: 'overlay',
        fontSize: 'Z2',
        color: 'caption',
        lineHeight: '1.5',
        text: (el, s) => _bodyText(s.root?.suggestionConfirm?.item)
      }
    },

    // One-line metadata: time, assignee, tags, recipient, etc.
    MetaLine: {
      hide: (el, s) => !_metaLine(s.root?.suggestionConfirm?.bucket, s.root?.suggestionConfirm?.item),
      tag: 'span',
      fontSize: 'Y2',
      color: 'caption',
      text: (el, s) => _metaLine(s.root?.suggestionConfirm?.bucket, s.root?.suggestionConfirm?.item)
    },

    ModalFooter: {
      flow: 'x',
      gap: 'Z1',

      ConfirmBtn: {
        extends: 'Button',
        theme: 'primary',
        text: (el, s) => {
          const c = s.root?.suggestionConfirm
          if (!c) return 'Confirm'
          const { verb, label } = _verbLabel(c.bucket, c.item?.action)
          return `${verb} ${label}`
        },
        onClick: async (e, el, s) => {
          const c = s.root?.suggestionConfirm
          if (!c) return

          // Merge user's title edit back into the item before forwarding
          const editedItem = { ...c.item }
          const custom = (s.root?.suggestionConfirmTitle || '').trim()
          if (c.bucket === 'chat_messages') {
            if (custom) editedItem.body = custom
          } else {
            if (custom) editedItem.title = custom
          }

          const targetId = await el.call('applySuggestion', c.bucket, editedItem)
          if (!targetId) return // applySuggestion failed — keep modal open

          s.root?.update({ suggestionConfirm: null, suggestionConfirmTitle: null })
          const route = SUGGESTION_BUCKETS[c.bucket]?.route
          if (route) routeTo(el, route)
        }
      }
    }
  }
}
